// 管理画面の認証（HttpOnly Cookie セッション方式）
//   ログイン画面でパスワードを入力 → POST /api/admin/login → サーバーが
//   HttpOnly; Secure; SameSite=Lax の Cookie（admin_session）を立てる。
//   トークンは JS から読めない Cookie に入るため、sessionStorage / localStorage に
//   依存しない（iOS Safari のプライベートブラウズやストレージ制限の影響を受けない）。
//   以降の API 呼び出しは同一オリジンのため Cookie が自動送信される。

export const ADMIN_LOGOUT_EVENT = 'admin-logout';

// Cookie は HttpOnly のため JS からは読めない。認証状態はサーバーに問い合わせて判定する。
export async function checkAdminLoggedIn(): Promise<boolean> {
  try {
    const res = await fetch('/api/admin/me', { credentials: 'include' });
    return res.ok;
  } catch {
    return false;
  }
}

export interface AdminUser { id: number | null; login_id: string | null; name: string; legacy?: boolean }

// ログイン中の管理者（未ログインなら null）
export async function fetchAdminMe(): Promise<AdminUser | null> {
  try {
    const res = await fetch('/api/admin/me', { credentials: 'include' });
    if (!res.ok) return null;
    const d = await res.json().catch(() => ({} as any));
    return d?.user || null;
  } catch {
    return null;
  }
}

// loginId が空なら移行期間の共有パスワード方式（管理者アカウント登録後はサーバーが拒否）
export async function adminLogin(loginId: string, password: string): Promise<void> {
  const res = await fetch('/api/admin/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ login_id: loginId.trim(), password }),
    credentials: 'include',
  });
  const data = await res.json().catch(() => ({} as any));
  if (!res.ok) {
    throw new Error(data?.error || `ログインに失敗しました (${res.status})`);
  }
  // トークンの保存は不要（サーバーが Set-Cookie でセッションを確立済み）。
}

export async function adminLogout(): Promise<void> {
  try {
    await fetch('/api/admin/logout', { method: 'POST', credentials: 'include' });
  } catch {
    // ネットワークエラーでもローカル状態はログアウト扱いにする
  }
  window.dispatchEvent(new Event(ADMIN_LOGOUT_EVENT));
}

// fetch 用の共通ヘッダを返すヘルパ。
// Cookie 方式では Authorization は不要だが、呼び出し側の互換のため関数は残す。
// （同一オリジンの fetch は既定で Cookie を送るため、ヘッダに認証情報は載せない）
export function adminHeaders(extra?: Record<string, string>): Record<string, string> {
  return { ...(extra || {}) };
}

// 管理APIが 401 を返したら（期限切れなど）自動でログアウト → ログイン画面へ
let installed = false;
export function installAdminAuthGuard(): void {
  if (installed) return;
  installed = true;
  const original = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    // 同一オリジンの API 呼び出しに Cookie を確実に載せる
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    let nextInit = init;
    if (url.includes('/api/')) {
      nextInit = { ...(init || {}), credentials: init?.credentials ?? 'include' };
    }
    const res = await original(input, nextInit);
    if (res.status === 401 && url.includes('/api/admin/') && !url.includes('/api/admin/login')) {
      window.dispatchEvent(new Event(ADMIN_LOGOUT_EVENT));
    }
    return res;
  };
}
